/**
 * EmailDomainSection (#3298)
 *
 * The workshop's own sending domain for customer email. The admin enters a
 * domain and a from address, publishes the DNS records Resend hands back, and
 * customer email moves from the platform address to e.g. huolto@korjaamo.fi
 * once Resend has verified them. Until then (and whenever the records break)
 * email keeps going out from the platform address, so nothing is lost.
 *
 * Gated server-side by `settings.manage_branding` and the `customDomain`
 * entitlement; this component only mirrors that.
 *
 * A domain that already existed on the shared Resend account is adopted
 * (#3710): it also needs the `_os-buddy-verify` TXT record, shown as the first
 * DNS row, before customer email moves to it.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api, ApiError } from '@/lib/api';
import { useNotify } from '@/hooks/useNotify';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

type EmailDomainStatus = 'not_started' | 'pending' | 'verified' | 'failed' | 'temporary_failure';

interface EmailDomainRecord {
  record: string;
  type: string;
  name: string;
  value: string;
  ttl: string;
  status: EmailDomainStatus;
  priority?: number;
}

interface EmailDomainOwnership {
  name: string;
  value: string;
  status: 'pending' | 'verified';
  verifiedAt: string | null;
}

interface TenantEmailDomain {
  domain: string;
  fromAddress: string;
  status: EmailDomainStatus;
  records: EmailDomainRecord[];
  lastCheckedAt: string;
  verifiedAt: string | null;
  lastError: string | null;
  adopted?: boolean;
  ownership?: EmailDomainOwnership | null;
}

interface EmailDomainResponse {
  entitled: boolean;
  available: boolean;
  platformFromAddress: string;
  domain: TenantEmailDomain | null;
}

const QUERY_KEY = ['tenant', 'email-domain'] as const;

/** Mirrors the server rule: an adopted domain needs the TXT proof before it sends. */
function ownershipPending(domain: TenantEmailDomain): boolean {
  return domain.adopted === true && domain.ownership?.status !== 'verified';
}

/** Resend's status, held at `pending` while an adopted domain's ownership is unproven. */
function effectiveStatus(domain: TenantEmailDomain): EmailDomainStatus {
  return domain.status === 'verified' && ownershipPending(domain) ? 'pending' : domain.status;
}

/** DNS rows to show: the ownership challenge (if any) first, then Resend's records. */
function dnsRows(domain: TenantEmailDomain): EmailDomainRecord[] {
  const ownership = domain.adopted ? domain.ownership : null;
  if (!ownership) return domain.records;
  return [
    { record: 'OWNERSHIP', type: 'TXT', name: ownership.name, value: ownership.value, ttl: 'Auto', status: ownership.status },
    ...domain.records,
  ];
}

const STATUS_COLOR: Record<EmailDomainStatus, 'success' | 'warning' | 'error' | 'default'> = {
  verified: 'success',
  pending: 'warning',
  not_started: 'default',
  temporary_failure: 'warning',
  failed: 'error',
};

const KNOWN_ERROR_CODES = new Set([
  'invalid_domain',
  'shared_mailbox_domain',
  'platform_domain',
  'invalid_from_address',
  'from_address_domain_mismatch',
  'email_domain_taken',
  'email_domain_rejected',
  'email_provider_unavailable',
  'entitlement_required',
]);

function errorKey(err: unknown): string {
  const code =
    err instanceof ApiError && err.data && typeof err.data === 'object'
      ? (err.data as { code?: unknown }).code
      : undefined;
  return typeof code === 'string' && KNOWN_ERROR_CODES.has(code)
    ? `branding.emailDomain.errors.${code}`
    : 'branding.emailDomain.errors.generic';
}

interface EmailDomainSectionProps {
  canManage: boolean;
}

export function EmailDomainSection({ canManage }: EmailDomainSectionProps) {
  const { t, i18n } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [domainInput, setDomainInput] = useState('');
  const [fromInput, setFromInput] = useState('');
  const [editingFrom, setEditingFrom] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<EmailDomainResponse>('/tenants/me/email-domain'),
    enabled: canManage,
  });

  const onSuccess = (data: EmailDomainResponse) => {
    queryClient.setQueryData(QUERY_KEY, data);
  };
  const onError = (err: unknown) => {
    notify.error(tc('error'), t(errorKey(err)));
  };

  const save = useMutation({
    mutationFn: (body: { domain: string; fromAddress: string }) =>
      api.put<EmailDomainResponse>('/tenants/me/email-domain', body),
    onSuccess: (data) => {
      onSuccess(data);
      setEditingFrom(null);
      notify.success(t('branding.emailDomain.saved'));
    },
    onError,
  });

  const verify = useMutation({
    mutationFn: () => api.post<EmailDomainResponse>('/tenants/me/email-domain/verify'),
    onSuccess: (data) => {
      onSuccess(data);
      notify.info(
        data.domain && effectiveStatus(data.domain) === 'verified'
          ? t('branding.emailDomain.verifiedNow')
          : t('branding.emailDomain.checkStarted'),
      );
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: () => api.delete<EmailDomainResponse>('/tenants/me/email-domain'),
    onSuccess: (data) => {
      onSuccess(data);
      setConfirmRemove(false);
      setDomainInput('');
      setFromInput('');
      notify.success(t('branding.emailDomain.removed'));
    },
    onError: (err) => {
      setConfirmRemove(false);
      onError(err);
    },
  });

  const copy = (value: string) => {
    void navigator.clipboard.writeText(value).then(() => {
      notify.success(t('branding.emailDomain.copied'));
    });
  };

  if (!canManage || !query.data) return null;
  const { entitled, available, platformFromAddress, domain } = query.data;
  const status = domain ? effectiveStatus(domain) : null;
  const busy = save.isPending || verify.isPending || remove.isPending;

  const formatTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : '–';

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('branding.emailDomain.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('branding.emailDomain.description', { platform: platformFromAddress })}
      </Typography>

      {!entitled ? (
        <Alert severity="info">{t('branding.emailDomain.notEntitled')}</Alert>
      ) : !available ? (
        <Alert severity="info">{t('branding.emailDomain.unavailable')}</Alert>
      ) : !domain ? (
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            label={t('branding.emailDomain.domain')}
            placeholder={t('branding.emailDomain.domainPlaceholder')}
            helperText={t('branding.emailDomain.domainHint')}
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            type="email"
            label={t('branding.emailDomain.fromAddress')}
            placeholder={t('branding.emailDomain.fromAddressPlaceholder', {
              domain: domainInput.trim() || t('branding.emailDomain.domainPlaceholder'),
            })}
            helperText={t('branding.emailDomain.fromAddressHint')}
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={busy || !domainInput.trim() || !fromInput.trim()}
              onClick={() => save.mutate({ domain: domainInput.trim(), fromAddress: fromInput.trim() })}
            >
              {t('branding.emailDomain.add')}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {domain.domain}
            </Typography>
            <Chip
              size="small"
              color={STATUS_COLOR[status ?? domain.status]}
              label={t(`branding.emailDomain.status.${status ?? domain.status}`)}
            />
          </Stack>

          <Alert severity={status === 'verified' ? 'success' : status === 'failed' ? 'error' : 'warning'}>
            {status === 'verified'
              ? t('branding.emailDomain.sendingFrom', { address: domain.fromAddress })
              : ownershipPending(domain)
                ? t('branding.emailDomain.ownershipPending', { platform: platformFromAddress, name: domain.ownership?.name ?? '' })
                : t('branding.emailDomain.notYetSending', { platform: platformFromAddress })}
          </Alert>

          {domain.lastError && (
            <Typography variant="body2" color="error">
              {t('branding.emailDomain.lastError', { message: domain.lastError })}
            </Typography>
          )}

          {editingFrom === null ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                {t('branding.emailDomain.fromAddress')}: <strong>{domain.fromAddress}</strong>
              </Typography>
              <Button size="small" disabled={busy} onClick={() => setEditingFrom(domain.fromAddress)}>
                {tc('edit')}
              </Button>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                type="email"
                sx={{ flex: 1 }}
                label={t('branding.emailDomain.fromAddress')}
                helperText={t('branding.emailDomain.fromAddressHint')}
                value={editingFrom}
                onChange={(e) => setEditingFrom(e.target.value)}
              />
              <Box>
                <Button
                  variant="contained"
                  disabled={busy || !editingFrom.trim()}
                  onClick={() => save.mutate({ domain: domain.domain, fromAddress: editingFrom.trim() })}
                >
                  {tc('save')}
                </Button>
                <Button disabled={busy} onClick={() => setEditingFrom(null)}>
                  {tc('cancel')}
                </Button>
              </Box>
            </Stack>
          )}

          <Typography variant="subtitle2">{t('branding.emailDomain.dnsTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('branding.emailDomain.dnsHint', { domain: domain.domain })}
          </Typography>
          <TableContainer>
            <Table size="small" aria-label={t('branding.emailDomain.dnsTitle')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('branding.emailDomain.recordType')}</TableCell>
                  <TableCell>{t('branding.emailDomain.recordName')}</TableCell>
                  <TableCell>{t('branding.emailDomain.recordValue')}</TableCell>
                  <TableCell>{t('branding.emailDomain.recordPriority')}</TableCell>
                  <TableCell>{t('branding.emailDomain.recordStatus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dnsRows(domain).map((record) => (
                  <TableRow key={`${record.record}-${record.type}-${record.name}`}>
                    <TableCell>{record.type}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Box component="code" sx={{ wordBreak: 'break-all' }}>
                          {record.name}
                        </Box>
                        <Tooltip title={t('branding.emailDomain.copy')}>
                          <IconButton size="small" aria-label={t('branding.emailDomain.copy')} onClick={() => copy(record.name)}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Box component="code" sx={{ wordBreak: 'break-all' }}>
                          {record.value}
                        </Box>
                        <Tooltip title={t('branding.emailDomain.copy')}>
                          <IconButton size="small" aria-label={t('branding.emailDomain.copy')} onClick={() => copy(record.value)}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>{record.priority ?? '–'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={STATUS_COLOR[record.status]}
                        label={t(`branding.emailDomain.status.${record.status}`)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary">
            {t('branding.emailDomain.lastChecked', { time: formatTime(domain.lastCheckedAt) })}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button color="error" disabled={busy} onClick={() => setConfirmRemove(true)}>
              {t('branding.emailDomain.remove')}
            </Button>
            <Button variant="contained" startIcon={<RefreshIcon />} disabled={busy} onClick={() => verify.mutate()}>
              {t('branding.emailDomain.verify')}
            </Button>
          </Stack>

          <ConfirmDialog
            open={confirmRemove}
            title={t('branding.emailDomain.removeTitle')}
            message={t('branding.emailDomain.removeMessage', { domain: domain.domain, platform: platformFromAddress })}
            confirmText={t('branding.emailDomain.remove')}
            confirmColor="error"
            loading={remove.isPending}
            onConfirm={() => remove.mutate()}
            onCancel={() => setConfirmRemove(false)}
          />
        </Stack>
      )}
    </Paper>
  );
}
